import React, { useState } from "react";
import Papa from "papaparse";
import { db } from "@/config/firebase";
import { addDoc, collection, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { normalizeMapUrl } from "@/utils/maps";

const DEFAULT_URL = process.env.REACT_APP_DEFAULT_EVENTS_CSV_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_DEFAULT_EVENTS_CSV_URL) || "";
const mapsFromLocation = (loc) => (loc ? normalizeMapUrl(loc, loc) : "");

export default function EventsImport() {
  const { user, role } = useAuth();
  const [rows, setRows] = useState([]);
  const [url, setUrl] = useState(DEFAULT_URL);
  const [status, setStatus] = useState("");
  const [paste, setPaste] = useState("");

  const parse = (csv) => new Promise((resolve, reject)=>{
    Papa.parse(csv, { header:true, skipEmptyLines:true, complete: r=>resolve(r.data||[]), error: reject });
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; const text = await f.text(); const data = await parse(text); setRows(data);
  };

  const loadFromUrl = async () => {
    setStatus("Lade CSV von URL…");
    try { const res = await fetch(url); const txt = await res.text(); const data = await parse(txt); setRows(data); setStatus(`Geladen: ${data.length} Zeilen`); }
    catch { setStatus("Fehler beim Laden der CSV"); }
  };

  const normBool = (v) => { if (typeof v==='boolean') return v; const s=String(v||"").toLowerCase(); return s==='true'||s==='1'||s==='ja'||s==='y'||s==='yes'; };

  const importNow = async () => {
    if (role !== 'admin') { setStatus('Nur Admin darf Events importieren.'); return; }
    
    setStatus("🔍 Prüfe auf Duplikate...");
    
    // Load existing events to check for duplicates
    const existingEventsSnap = await getDocs(collection(db, "events"));
    const existingEvents = existingEventsSnap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      // Convert Timestamp to Date for comparison
      start_datetime: d.data().start_datetime?.toDate?.() || d.data().start_datetime
    }));
    
    let created=0, errors=0, skipped=0;
    
    for (const r of rows) {
      try {
        const title = r.title || r.Titel || r.matchup || r.bezeichnung || "Spiel";
        const type = (r.type || r.art || "game").toString();
        const startStr = r.start_datetime || r.start || `${r.date||""} ${r.time||""}`;
        const start = startStr ? new Date(startStr) : new Date();
        const location = r.location || r.ort || r.address || r.halle || "";
        const opponent = r.opponent || r.gegner || r.oppo || "";
        const league = r.league || r.liga || "";
        const team_home = normBool(r.team_home);
        const gym_name = r.gym_name || r.hallenname || "";
        const maps_url = r.maps_url || mapsFromLocation(location || gym_name);
        
        // Check for duplicate ONLY for games (not training)
        // Trainings sind oft identisch und sollen nicht als Duplikate erkannt werden
        const isDuplicate = type === 'game' && existingEvents.some(existing => {
          // Nur bei Games prüfen
          if (existing.type !== 'game') return false;
          
          const existingStart = existing.start_datetime instanceof Date 
            ? existing.start_datetime 
            : new Date(existing.start_datetime);
          
          const timeDiff = Math.abs(existingStart - start) / 1000 / 60; // minutes
          
          return (
            existing.title === title &&
            existing.opponent === opponent &&
            timeDiff < 60 // Same event if within 1 hour
          );
        });
        
        if (isDuplicate) {
          console.log(`⏭️ Skipped duplicate: ${title} - ${opponent} at ${start}`);
          skipped++;
          continue;
        }
        
        await addDoc(collection(db, "events"), { 
          title, 
          type, 
          start_datetime: start, 
          location, 
          opponent, 
          league, 
          team_home, 
          gym_name, 
          maps_url, 
          status: "scheduled", 
          created_by: user?.uid || "", 
          created_at: serverTimestamp() 
        });
        
        created++;
        setStatus(`Import läuft... ${created} neu, ${skipped} übersprungen`);
      } catch (e) { 
        console.error('Import error:', e);
        errors++; 
      }
    }
    
    setStatus(`✅ Import fertig! Neu: ${created}, Übersprungen: ${skipped}, Fehler: ${errors}`);
  };

  const parseScheduleText = () => {
    // Heuristik für DBB-Text: Zeilen mit Datum (dd.mm.yyyy) und Uhrzeit (hh:mm);
    // Gegnerermittlung anhand "RheinStars Köln 5" links/rechts vom Bindestrich.
    const TEAM = "RheinStars Köln 5";
    const lines = (paste||"").split(/\n+/).map(s=>s.trim()).filter(Boolean);
    const out = [];
    for (const ln of lines) {
      const dm = ln.match(/(\d{1,2})\.(\d{1,2})\.(\d{4}).*?(\d{1,2}:\d{2})/);
      if (!dm) continue;
      const dateStr = `${dm[3]}-${dm[2].padStart(2,'0')}-${dm[1].padStart(2,'0')}T${dm[4]}`;
      let opponent = ""; let team_home = false; let title = "Spiel";
      const vs = ln.split(/-|–|:|vs\.?/i);
      if (vs.length>=2) {
        const left = vs[0]; const right = vs[1];
        if (left.includes(TEAM)) { opponent = right.trim(); team_home = true; title = `${TEAM} vs ${opponent}`; }
        else if (right.includes(TEAM)) { opponent = left.trim(); team_home = false; title = `${opponent} vs ${TEAM}`; }
      }
      const hallMatch = ln.match(/Halle:?\s*([^,;]+)/i);
      const gym_name = hallMatch ? hallMatch[1].trim() : "";
      out.push({ title, type:"game", start_datetime: dateStr, location: gym_name, opponent, league: "", team_home, gym_name, maps_url: mapsFromLocation(gym_name) });
    }
    setRows(out);
    setStatus(`Erkannt: ${out.length} Spiele aus Text.`);
  };

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-4">Saisonspiele importieren</h2>
      <Card className="bg-[#1a0d11] border-white/10 mb-4">
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-2 md:grid-cols-3">
            <Input data-testid="events-url" value={url} onChange={e=>setUrl(e.target.value)} placeholder="CSV-URL" className="bg-[#2a1419] border-white/10 text-rose-50 md:col-span-2" />
            <Button data-testid="events-load-url" onClick={loadFromUrl} className="bg-white/10 hover:bg-white/20">Von URL laden</Button>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="events-file" type="file" accept=".csv" onChange={onFile} />
            <Button data-testid="events-import" onClick={importNow} className="bg-[#b40023] hover:bg-[#97001d]">Import starten</Button>
          </div>
          {status && <p data-testid="events-status" className="text-sm text-rose-200/80">{status}</p>}
        </CardContent>
      </Card>

      <Card className="bg-[#1a0d11] border-white/10">
        <CardContent className="pt-4 space-y-2">
          <h3 className="font-semibold">Spielplan-Text einfügen</h3>
          <textarea value={paste} onChange={e=>setPaste(e.target.value)} placeholder="Text aus der DBB-Seite hier einfügen…" className="w-full h-40 p-2 rounded bg-[#2a1419] border-white/10 text-rose-50"></textarea>
          <div className="flex gap-2">
            <Button data-testid="events-parse-text" onClick={parseScheduleText} className="bg-white/10 hover:bg-white/20">Aus Text erkennen</Button>
            <Button data-testid="events-import-text" onClick={importNow} className="bg-[#b40023] hover:bg-[#97001d]">Erkannte Spiele importieren</Button>
          </div>
          <p className="text-xs text-rose-200/60">Heuristik erkennt Datum/Uhrzeit, Gegner und Halle; bitte prüfen vor Import.</p>
        </CardContent>
      </Card>
    </div>
  );
}
